/**
 * เตรียมรูปก่อนส่งเข้า feedback — server รับเฉพาะ PNG / JPEG / WEBP และรูปละไม่เกิน ~3.75MB (base64 5MB)
 * (backend/app/services/feedback_service.py decode_images)
 *
 * E2E 27 ต.ค. 2569 (B7): เดิม FE รับ image/* ทุกชนิดและไม่ย่อขนาด → แนบ GIF / รูปมือถือ 4–8MB
 * แล้ว "ทั้งเรื่อง" ถูกปฏิเสธด้วยข้อความ "ไม่ใช่ไฟล์รูป" · ตอนนี้แปลงเป็น JPEG ฝั่ง browser ก่อนส่ง
 * ถ้า browser เปิดรูปไม่ได้ (เช่น HEIC บน Chrome) คืน null ให้ผู้เรียกข้ามไฟล์นั้นพร้อมบอกผู้ใช้
 */

export const SERVER_IMAGE_TYPES: ReadonlyArray<string> = ['image/png', 'image/jpeg', 'image/webp'];

/** เผื่อจาก ~3.75MB ของ server — base64 โต 4/3 */
export const MAX_IMAGE_BYTES = 3_000_000;

const MAX_SIDE_STEPS = [2560, 1920, 1280];

export const needsImageConversion = (file: File): boolean =>
  !SERVER_IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES;

export async function prepareFeedbackImage(file: File): Promise<File | null> {
  if (!needsImageConversion(file)) return file;
  if (typeof createImageBitmap !== 'function') return null;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }
  try {
    for (const maxSide of MAX_SIDE_STEPS) {
      const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#fff'; // GIF/PNG โปร่งใส → พื้นขาว (JPEG ไม่มี alpha)
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85));
      if (blob && blob.size <= MAX_IMAGE_BYTES) {
        const base = (file.name || 'image').replace(/\.[^.]+$/, '');
        return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
      }
    }
    return null;
  } finally {
    bitmap.close?.();
  }
}

/** ความยาวแบบนับตัวอักษรจริง (code point) ให้ตรงกับ len() ของ Python — emoji 1 ตัว = 1 ไม่ใช่ 2 (B16) */
export const codePointLength = (s: string): number => Array.from(s).length;
